import React, { useState, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Monitor, Smartphone } from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import Home from "@/pages/site/Home";
import HowItWorks from "@/pages/site/HowItWorks";
import About from "@/pages/site/About";
import Faq from "@/pages/site/Faq";
import Contact from "@/pages/site/Contact";

const PAGE_COMP = { home: Home, how_it_works: HowItWorks, about: About, faq: Faq, contact: Contact };
// Logical device widths. Rendering inside an iframe at these widths makes the site's
// own responsive breakpoints (sm/md/lg) behave exactly as they would on that device.
const LOGICAL = { desktop: 1280, mobile: 390 };

export default function LivePreview({ draft, page = "home" }) {
  const [device, setDevice] = useState("desktop");
  const PageComp = PAGE_COMP[page] || Home;
  const mobile = device === "mobile";
  const logicalWidth = LOGICAL[device];

  const frameRef = useRef(null);      // scrolling pane
  const iframeRef = useRef(null);
  const [mountNode, setMountNode] = useState(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  // (Re)build the iframe document (inject the app's stylesheets so the preview looks real).
  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let raf;
    const init = () => {
      const doc = iframe.contentDocument;
      if (!doc || !doc.body) { raf = requestAnimationFrame(init); return; }
      doc.head.innerHTML = "";
      const vp = doc.createElement("meta");
      vp.name = "viewport"; vp.content = `width=${logicalWidth}, initial-scale=1`;
      doc.head.appendChild(vp);
      document.querySelectorAll('style, link[rel="stylesheet"]').forEach((n) => {
        try { doc.head.appendChild(n.cloneNode(true)); } catch (e) { /* ignore */ }
      });
      // Force reveal-on-scroll (framer-motion whileInView) elements visible and neutralise
      // sticky headers inside the static preview document.
      const ov = doc.createElement("style");
      ov.textContent = "*{opacity:1 !important;animation:none !important} .sticky,[style*='position: sticky'],[style*='position:sticky']{position:static !important}";
      doc.head.appendChild(ov);
      doc.documentElement.style.width = logicalWidth + "px";
      doc.body.style.margin = "0";
      doc.body.style.width = logicalWidth + "px";
      doc.body.style.background = "#FDFBF7";
      setMountNode(doc.body);
    };
    init();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [device, logicalWidth]);

  // Reset reserved height + scroll when the page or device changes so a shorter page
  // doesn't inherit a taller page's height (avoids a min-height feedback loop).
  useLayoutEffect(() => {
    setContentHeight(0);
    if (frameRef.current) frameRef.current.scrollTop = 0;
  }, [device, page]);

  // Compute the scale so the device viewport fills the available pane width, and the height to reserve.
  useLayoutEffect(() => {
    const compute = () => {
      const fw = frameRef.current?.clientWidth;
      const body = iframeRef.current?.contentDocument?.body;
      if (!fw || !body) return;
      const s = mobile ? fw / logicalWidth : Math.min(fw / logicalWidth, 1);
      setScale(s);
      setContentHeight(body.scrollHeight || 0);
    };
    compute();
    const raf = requestAnimationFrame(compute);
    const timers = [setTimeout(compute, 150), setTimeout(compute, 600), setTimeout(compute, 1200)];
    const observers = [];
    const body = iframeRef.current?.contentDocument?.body;
    if (window.ResizeObserver) {
      if (body) { const ro = new ResizeObserver(compute); ro.observe(body); observers.push(ro); }
      if (frameRef.current) { const ro2 = new ResizeObserver(compute); ro2.observe(frameRef.current); observers.push(ro2); }
    }
    return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); observers.forEach((o) => o.disconnect()); };
  }, [device, logicalWidth, draft, page, mountNode, mobile]);

  return (
    <div className="sticky top-24" data-testid="cms-live-preview">
      <div className="flex items-center justify-between mb-3">
        <p className="uppercase tracking-[0.15em] text-[10px] font-bold text-[#787672]">Live preview · unsaved</p>
        <div className="inline-flex rounded-full border border-[#E8E6E1] p-1 bg-white">
          <button data-testid="preview-desktop" onClick={() => setDevice("desktop")} className={`w-8 h-7 rounded-full flex items-center justify-center transition-colors ${!mobile ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}><Monitor className="w-4 h-4" /></button>
          <button data-testid="preview-mobile" onClick={() => setDevice("mobile")} className={`w-8 h-7 rounded-full flex items-center justify-center transition-colors ${mobile ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}><Smartphone className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="rounded-2xl border border-[#E8E6E1] bg-[#FDFBF7] overflow-hidden shadow-[0_12px_40px_rgba(44,53,49,0.10)]">
        <div className="flex items-center gap-1.5 px-4 h-9 border-b border-[#F0EFEB] bg-white">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E58B8B]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E8C46B]" /><span className="w-2.5 h-2.5 rounded-full bg-[#8BC1A0]" />
        </div>
        <div ref={frameRef} className="cms-preview overflow-y-auto overflow-x-hidden bg-[#FDFBF7]" style={{ height: "calc(100vh - 220px)" }}>
          <div className={`mx-auto ${mobile ? "shadow-[0_0_0_1px_#F0EFEB]" : ""}`} style={{ width: logicalWidth * scale, height: contentHeight * scale }}>
            <iframe
              ref={iframeRef}
              title="Live site preview"
              scrolling="no"
              style={{ width: logicalWidth, height: contentHeight || 10, border: 0, display: "block", transform: `scale(${scale})`, transformOrigin: "top left", pointerEvents: "none" }}
            />
            {mountNode && createPortal(
              <>
                <SiteHeader settings={draft.settings} header={draft.header} />
                <PageComp previewSite={draft} />
                <SiteFooter settings={draft.settings} footer={draft.footer} />
              </>,
              mountNode
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
